import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, mountainDay } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Facts, Problem } from "@/components/PreviewBits";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";

export interface AccessKey {
  key: string;
  name: string;
  description?: string | null;
}

export type GrantKind = "trial" | "comp";

/** What's missing before a grant can be reviewed ("" = ready). Trials need an end date; comps may be open-ended. */
export function grantProblem(v: { key: string; kind: GrantKind; endsOn: string; reason: string }, today: string): string {
  if (!v.key) return "Choose what to give.";
  if (v.kind === "trial" && !v.endsOn) return "A trial needs an end date.";
  if (v.endsOn && v.endsOn <= today) return "The end date has to be after today.";
  if (!v.reason.trim()) return "Say why (it is kept on the contact's timeline).";
  return "";
}

/** The access key's own name ("MHP Masterclass"), else the key made readable. */
export const accessName = (keys: AccessKey[] | undefined, key: string) => keys?.find((k) => k.key === key)?.name || key.replace(/^course:/, "").replace(/[_-]/g, " ");

/** "Give access" button for the contact page's access list. */
export function GiveAccessButton({ contactId }: { contactId: number }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setOpen(true)}>
        <Plus className="size-3.5" /> Give access
      </Button>
      {open && <GiveAccessDialog contactId={contactId} onClose={() => setOpen(false)} />}
    </>
  );
}

/** access_grant: a trial (end date required) or a comp (end date or none), with the reason. Moves no money. */
export function GiveAccessDialog({ contactId, onClose }: { contactId: number; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const keys = useQuery({ queryKey: ["access-keys"], queryFn: () => api<{ keys: AccessKey[] }>("/api/v1/access-keys").then((r) => r.keys), staleTime: 60_000 });
  const [key, setKey] = useState("");
  const [kind, setKind] = useState<GrantKind>("comp");
  const [endsOn, setEndsOn] = useState("");
  const [reason, setReason] = useState("");
  const [review, setReview] = useState(false);
  const [problem, setProblem] = useState("");
  const today = mountainDay();
  const missing = grantProblem({ key, kind, endsOn, reason }, today);
  const sorted = [...(keys.data ?? [])].sort((a, b) => (a.name || a.key).localeCompare(b.name || b.key));
  const grant = useMutation({
    mutationFn: () =>
      api(`/api/v1/contacts/${contactId}/access`, { json: { key, kind, ends_on: endsOn || undefined, note: reason.trim() } }),
    onMutate: () => setProblem(""),
    onSuccess: () => {
      for (const k of ["billing-detail", "billing-summary", "contact", "timeline"]) void qc.invalidateQueries({ queryKey: [k] });
      toast(`Gave ${accessName(keys.data, key)}`);
      onClose();
    },
    onError: (e) => setProblem(e.message),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(520px,calc(100vw-2rem))]">
        <DialogTitle>{review ? "Check before giving access" : "Give access"}</DialogTitle>
        {!review ? (
          <>
            <DialogDescription>A free trial or a complimentary membership or course. No money moves and no card is needed. The person can sign in within about a minute.</DialogDescription>
            <div className="space-y-3 text-sm">
              <div className="space-y-1">
                <Label htmlFor="ga-key">What to give</Label>
                <Select id="ga-key" value={key} onChange={(e) => setKey(e.target.value)}>
                  <option value="">{keys.isLoading ? "Loading…" : "Choose…"}</option>
                  {sorted.map((k) => (
                    <option key={k.key} value={k.key}>
                      {k.name || k.key}
                    </option>
                  ))}
                </Select>
                {keys.error && <p className="text-xs text-[var(--color-danger-fg)]">Couldn't load the list: {keys.error.message}</p>}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="ga-kind">Kind</Label>
                  <Select id="ga-kind" value={kind} onChange={(e) => setKind(e.target.value as GrantKind)}>
                    <option value="comp">Complimentary</option>
                    <option value="trial">Free trial</option>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="ga-ends">Ends on {kind === "comp" ? "(leave empty for no end)" : "(required)"}</Label>
                  <Input id="ga-ends" type="date" min={today} value={endsOn} onChange={(e) => setEndsOn(e.target.value)} />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="ga-reason">Reason</Label>
                <Input id="ga-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Speaker comp for the Boot Camp / 14-day Club trial" />
              </div>
            </div>
          </>
        ) : (
          <div className="space-y-2">
            <Facts
              rows={[
                ["Gives", accessName(keys.data, key)],
                ["Kind", kind === "trial" ? "Free trial" : "Complimentary"],
                ["Ends", endsOn ? `${formatDate(endsOn)} (at the start of that day, Mountain Time)` : "No end date"],
                ["Reason", reason.trim()],
              ]}
            />
            <p className="text-xs text-[var(--color-muted-foreground)]">No email is sent. You can take it back with Remove access.</p>
          </div>
        )}
        <Problem text={problem} />
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          {!review && missing && key && <span className="mr-auto text-xs text-[var(--color-muted-foreground)]">{missing}</span>}
          <Button variant="outline" onClick={review ? () => setReview(false) : onClose}>
            {review ? "Back" : "Cancel"}
          </Button>
          {!review ? (
            <Button disabled={!!missing} onClick={() => setReview(true)}>
              Review
            </Button>
          ) : (
            <Button disabled={grant.isPending} onClick={() => grant.mutate()}>
              Give access
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
